// Shared retry policy + cache TTL constants for the apiFetch wrappers
// in apps/web-rnw/src/api.ts and apps/mobile/src/api/index.ts. Lifted in
// 1.34.0 so the policy stays in sync between the two surfaces — a
// drift in MAX_RETRIES or BASE_BACKOFF_MS would be hard to notice
// from a single-surface reading and could subtly change retry
// economics under throttling spikes.
//
// Why these values (per the original WEB-6 commit, 0.67.0):
//
//   * RETRYABLE_ANY_METHOD = {429}. API GW throttle is always
//     pre-execution — the request never reaches Lambda, retrying is
//     safe regardless of method.
//
//   * RETRYABLE_GET_ONLY = {429, 503}. 503 (Lambda concurrency
//     saturation, internal errors mapped down) is usually
//     pre-execution, but a server-side timeout-after-write would
//     surface here too. Retrying a non-GET could double-apply the
//     side effect. Cap 503 retries to GETs; non-GETs bubble up
//     immediately, and the Idempotency-Key header on every non-GET
//     means user-driven retries land at the server's idempotency
//     middleware (deduped via DDB cache).
//
//   * MAX_RETRIES = 4 + BASE_BACKOFF_MS = 250. Worst-case backoff
//     arc is 250 + 500 + 1000 + 2000 = ~15s with the random ±100ms
//     jitter, which is enough to ride out a saturation spike on a
//     hot DDB partition without burning the user's patience.

//   * GET_DEADLINE_MS = 30_000. OP-35. Nothing in the read path had a
//     clock: apiFetch awaits getIdToken() and then fetch(), and neither
//     can time out on its own — the Cognito SDK wrappers in
//     apps/mobile/src/auth/cognito.ts resolve only from a callback that
//     may never fire, and React Native's fetch ships with no timeout. On
//     2026-09-15 that left Progress spinning for six minutes while
//     CloudWatch showed the backend had received two requests, both under
//     a second: the calls never left the device.
//     30s is not a taste — it is API Gateway's own hard integration
//     ceiling (Lambda is set tighter still, 20s). Past it no response can
//     arrive from our backend by construction, so waiting is provably
//     pointless. It also clears the worst-case backoff arc above, so a
//     legitimate retry sequence is not cut off by the clock.
//     ⛔ That arc is ~4s, NOT the "~15s" the comment above claims:
//     250 + 500 + 1000 + 2000 = 3750ms. The wrong figure predates this
//     line; it is corrected here rather than repeated, because this
//     paragraph leans on it as a justification.
//     ⛔ GETs only. A non-GET that gives up on its own would abandon a
//     workout the athlete just finished; a read that gives up shows an
//     error and can be pulled again.

export const RETRYABLE_ANY_METHOD: ReadonlySet<number> = new Set([429]);
export const RETRYABLE_GET_ONLY: ReadonlySet<number> = new Set([429, 503]);
export const MAX_RETRIES = 4;
export const BASE_BACKOFF_MS = 250;
export const GET_DEADLINE_MS = 30_000;
// One clock for the whole token step (session read plus any refresh), see
// sessionReader.ts. Half of GET_DEADLINE_MS, so a stuck Cognito call fails as
// `auth_timeout` before the GET it holds up fails as `api_timeout`.
export const SESSION_DEADLINE_MS = 15_000;

// The host's timers, described structurally instead of imported — the same
// remedy this package already uses for `AbortLike` (asyncList.ts:17-20),
// because the shared tsconfig deliberately omits the DOM lib: it is consumed
// by both a browser and a Hermes target.
type TimerHost = {
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
};
const timers = globalThis as unknown as TimerHost;

/** Resolves after `ms`, on the host's clock. */
export const delay = (ms: number): Promise<void> =>
  new Promise((done) => {
    timers.setTimeout(done, ms);
  });

/**
 * Race `work` against a deadline, aborting `ctrl` when it expires.
 *
 * Lives here, next to the constant it enforces, because BOTH apiFetch
 * copies (apps/mobile/src/api/index.ts, apps/web-rnw/src/api.ts) need it.
 * The L2 derogation that lets those two files stay separate is about the
 * TOKEN STORAGE they wrap (AsyncStorage vs localStorage) — this function
 * touches none of it, so copying it would have been the N+1 copy the
 * consolidation rule exists to prevent.
 *
 * Two things are injected rather than imported, because this package's
 * tsconfig deliberately omits the DOM lib (same reasoning, and the same
 * remedy, as `AbortLike` in asyncList.ts): `abort` is the caller's
 * `() => ctrl.abort()`, and `onTimeout` builds the error, because each app
 * owns its own ApiError class (a pre-existing, separately tracked
 * duplication). This decides WHEN; the caller decides what that means.
 */
export async function withDeadline<T>(
  work: Promise<T>,
  abort: () => void,
  ms: number,
  onTimeout: (ms: number) => Error,
): Promise<T> {
  // The loser of the race still rejects; without this its rejection is
  // unhandled and the runtime warns about a case we deliberately abandoned.
  work.catch(() => undefined);
  let timer: unknown;
  const clock = new Promise<never>((_resolve, reject) => {
    timer = timers.setTimeout(() => {
      abort();
      reject(onTimeout(ms));
    }, ms);
  });
  try {
    return await Promise.race([work, clock]);
  } finally {
    if (timer !== undefined) timers.clearTimeout(timer);
  }
}

// Cache TTL shared by both MAX data hooks (web + mobile). Lifted alongside
// the retry policy in 1.34.0: it should evolve at one site, not two.
//
// MAXES_CACHE_TTL_MS = 60_000: MaxesContext re-fetches `/api/me/maxes`
// when the in-memory snapshot is older than 60s. Short enough that a
// user switching tabs and editing a MAX sees a fresh value back in the
// logger; long enough that bouncing between PlanScreen and LoggerScreen
// in the same session doesn't burn requests.
export const MAXES_CACHE_TTL_MS = 60_000;
